/**
 * Responsive image grid with selection support.
 */
import React from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import type { MediaImage } from '@/types/media';
import { ImageCard } from './ImageCard';

interface ImageGridProps {
  images: MediaImage[];
  selectedIds: Set<string>;
  onSelectImage: (id: string, multiSelect: boolean) => void;
  onImageClick: (id: string) => void;
  isLoading?: boolean;
}

export function ImageGrid({
  images,
  selectedIds,
  onSelectImage,
  onImageClick,
  isLoading,
}: ImageGridProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="aspect-square bg-gray-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <PhotoIcon className="w-16 h-16 mb-4 text-gray-300" />
        <h3 className="text-lg font-medium text-gray-900 mb-1">No images yet</h3>
        <p className="text-sm">Upload images to get started</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
      {images.map((image) => (
        <ImageCard
          key={image.id}
          image={image}
          isSelected={selectedIds.has(image.id)}
          onSelect={onSelectImage}
          onClick={onImageClick}
        />
      ))}
    </div>
  );
}
